/*------------------------------------------------------------------------------
   About      : GA4 analytics manager. Owns the gtag.js lifecycle and is the
                single place all analytics calls route through, so swapping the
                backend later is a one-file change.

                - init()               : loads gtag.js, registers super properties
                - setUserProperties()  : role / id / tier (set once after login)
                - setSuperProperty()   : context sent with every event (active project…)
                - trackEvent()         : page_[feature]_[action] events
                - trackPageView()      : fired on every route change

                Event names live in ./AnalyticsEvents.ts. PII is stripped from
                every event via sanitize() per the taxonomy data-hygiene rules.

   Created on : Thu Sep 16 2021
   Author     : Siddharth Garg

   Copyright (c) 2021 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

import { resolveAppEnvironment }        from "features/contentManagement/mediaContent/mediaEnvMapping"
import { RunContextApp }                from "framework/RCApp"

// window.gtag / window.dataLayer are declared globally in src/types/typeUtils.ts

export const uuid = () => {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (dt + Math.random()*16)%16 | 0;
      dt = Math.floor(dt/16);
      return (c=='x' ? r :(r&0x3|0x8)).toString(16);
  });
  return uuid;
}

// Keys whose values must never reach analytics (substring match, case-insensitive).
const BLOCKED_KEY_FRAGMENTS = [
  'password', 'token', 'secret', 'apikey', 'api_key', 'jwt',
  'authorization', 'customer_id', 'config_value', 'access_token', 'email'
]

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

export type AnalyticsParams = Record<string, unknown>

export class AnalyticsScreenManager {

  private measurementId   : string = ''
  private superProperties : AnalyticsParams = {}
  private enabled         : boolean = false
  private debugMode       : boolean = false
  private timers          : Map<string, number> = new Map()

  public constructor(private rc : RunContextApp) {}

  /**
   * Loads gtag.js and registers super properties. Safe to call once at app
   * startup, before login. Disabled on localhost and productqa; measurement ID
   * is chosen from REACT_APP_TOOL_API_URL (deto dev / deto prod).
   */
  init(superProps : AnalyticsParams = {}) {

    if (typeof window !== 'undefined') {
      const host = window.location.hostname
      if (host === 'localhost' || host === '127.0.0.1') {
        this.rc.status(this.rc.getName(this), 'GA4 disabled — localhost')
        return
      }
    }

    const appEnv = resolveAppEnvironment()
    const measurementIds : Record<string, string> = {
      dev       : 'G-9JHC5R1TLC',  // deto dev
      uat       : 'G-5N2RNTJCH7',  // deto prod
      productqa : '',              // no GA stream
    }
    this.measurementId = measurementIds[appEnv] ?? ''

    if (!this.measurementId) {
      this.rc.status(this.rc.getName(this), 'GA4 disabled', appEnv)
      return
    }

    // Stream events into GA4 DebugView in real time everywhere except production.
    const runMode = (process.env.REACT_APP_ENV as string) || 'DEV'
    this.debugMode = runMode.toUpperCase() !== 'PROD'

    this.superProperties = {
      session_id : uuid(),
      ...superProps,
    }

    // Inject gtag.js dynamically so the analytics backend stays self-contained.
    if (typeof document !== 'undefined' && typeof window.gtag !== 'function') {
      const script = document.createElement('script')
      script.async = true
      script.src   = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`
      document.head.appendChild(script)

      window.dataLayer = window.dataLayer || []
      window.gtag = function gtag() { window.dataLayer.push(arguments) }
      window.gtag('js', new Date())
    }

    // send_page_view:false — this is an SPA, page views are fired on route change.
    // debug_mode:true routes events to DebugView (and shows them in Realtime fast).
    this.fire('config', this.measurementId, {
      send_page_view : false,
      debug_mode     : this.debugMode,
      ...this.superProperties,
    })

    this.enabled = true
    this.rc.status(this.rc.getName(this), 'GA4 initialised', this.measurementId)
  }

  /**
   * Identify the user. Called once after login completes. user_id should be a
   * stable, non-PII identifier (never the raw email).
   */
  setUserProperties(props : AnalyticsParams) {

    if (!this.canSend()) return

    const { user_id, ...rest } = props

    if (user_id != null) {
      this.fire('config', this.measurementId, { send_page_view : false, user_id })
    }
    this.fire('set', 'user_properties', this.sanitize(rest))
  }

  /**
   * Register / update a super property — context attached to every event
   * (e.g. active_project when the user switches projects).
   */
  setSuperProperty(key : string, value : unknown) {
    this.superProperties[key] = value
    if (this.canSend()) this.fire('set', { [key]: value })
  }

  /**
   * Track a user action. eventName must follow page_[feature]_[action].
   * Super properties and sanitised params are attached automatically.
   */
  trackEvent(eventName : string, params : AnalyticsParams = {}) {

    // Guard legacy call sites that pass non-string first args.
    if (typeof eventName !== 'string' || !eventName) return
    if (!this.canSend()) return

    this.fire('event', eventName, {
      ...this.superProperties,
      ...this.sanitize(params),
    })
  }

  /** Fired on every route change (see UiRouter.onLocationChange). */
  trackPageView(path : string, title ?: string) {
    if (!this.canSend()) return
    this.fire('event', 'page_view', {
      ...this.superProperties,
      page_path  : path,
      page_title : title ?? path,
    })
  }

  /** Start a named timer (workflow duration tracking). */
  startTimer(key : string) {
    this.timers.set(key, (typeof performance !== 'undefined' ? performance.now() : Date.now()))
  }

  /** Stop a named timer and fire an event with duration_ms / duration_sec attached. */
  endTimer(key : string, eventName : string, params : AnalyticsParams = {}) {
    const start = this.timers.get(key)
    if (start == null) return
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const duration_ms = Math.round(now - start)
    this.timers.delete(key)
    this.trackEvent(eventName, {
      ...params,
      duration_ms,
      duration_sec : Math.round(duration_ms / 1000),
    })
  }

  /**
   * Retained for compatibility with UiRouter.onNavEnd. Page views are fired
   * from UiRouter.onLocationChange (the single source of truth), so this is a
   * deliberate no-op to avoid double-counting.
   */
  onNavChange(_location : unknown, _action : unknown) {}

  // --- internals ------------------------------------------------------------

  private canSend() : boolean {
    return this.enabled && typeof window !== 'undefined' && typeof window.gtag === 'function'
  }

  private fire(...args : unknown[]) {
    try {
      window.gtag(...args)
    } catch (e) {
      this.rc.warn(this.rc.getName(this), 'gtag call failed', e)
    }
  }

  /** Strip blocklisted keys and scrub email patterns from string values. */
  private sanitize(params : AnalyticsParams) : AnalyticsParams {
    const clean : AnalyticsParams = {}
    for (const [key, value] of Object.entries(params)) {
      const keyLc = key.toLowerCase()
      if (BLOCKED_KEY_FRAGMENTS.some(fragment => keyLc.includes(fragment))) continue
      clean[key] = (typeof value === 'string') ? value.replace(EMAIL_REGEX, '[EMAIL]') : value
    }
    return clean
  }

}
